/**
 * Shopify Adapter (Story 12.4, Task 3)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses ShopifyQL via GraphQL for analytics data.
 *
 * Key Shopify specifics:
 * - ShopifyQL is the primary data source (aggregated analytics)
 * - GraphQL endpoint: https://{shop}/admin/api/{version}/graphql.json
 * - Auth header: X-Shopify-Access-Token
 * - No refresh token — offline access tokens are permanent
 * - Cost-based rate limiting (leaky bucket: 50 pts/sec standard)
 * - All ShopifyQL row cells are strings (require type parsing)
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { SHOP_DOMAIN_REGEX } from "./oauth";

// ============================================================================
// Constants
// ============================================================================

const SHOPIFY_API_VERSION = "2026-04"; // Bumped from 2026-01 — mandatory expiring tokens, sales reversal renames
const API_TIMEOUT_MS = 25_000;

// ============================================================================
// Types (local to adapter)
// ============================================================================

interface ShopifyQLColumn {
  name: string;
  dataType: string;
  displayName: string;
}

interface ShopifyQLTableData {
  columns: ShopifyQLColumn[];
  rows: string[][];
}

interface ShopifyQLResponse {
  data?: {
    shopifyqlQuery?: {
      tableData: ShopifyQLTableData | null;
      parseErrors: string[];
    };
  };
  errors?: Array<{ message: string; extensions?: { code?: string } }>;
  extensions?: {
    cost?: {
      requestedQueryCost: number;
      actualQueryCost: number;
      throttleStatus?: {
        maximumAvailable: number;
        currentlyAvailable: number;
        restoreRate: number;
      };
    };
  };
}

interface ShopifyShopResponse {
  data?: {
    shop?: {
      name: string;
    };
  };
  errors?: Array<{ message: string }>;
}

interface ShopifyAbandonedCheckoutsCountResponse {
  data?: {
    abandonedCheckoutsCount?: {
      count: number;
    };
  };
  errors?: Array<{ message: string }>;
}

interface ShopifyAbandonedCheckoutNode {
  subtotalPriceSet: { shopMoney: { amount: string } };
}

interface ShopifyAbandonedCheckoutsResponse {
  data?: {
    abandonedCheckouts?: {
      nodes: ShopifyAbandonedCheckoutNode[];
    };
  };
  errors?: Array<{ message: string }>;
}

interface ShopifyCustomerNode {
  amountSpent: { amount: string; currencyCode: string };
  numberOfOrders: string;
}

interface ShopifyCustomersResponse {
  data?: {
    customers?: {
      nodes: ShopifyCustomerNode[];
    };
  };
  errors?: Array<{ message: string }>;
}

interface ShopifyInventoryItemNode {
  unitCost: { amount: string } | null;
  inventoryLevels: {
    nodes: Array<{
      quantities: Array<{ name: string; quantity: number }>;
    }>;
  };
}

interface ShopifyInventoryResponse {
  data?: {
    inventoryItems?: {
      nodes: ShopifyInventoryItemNode[];
    };
  };
  errors?: Array<{ message: string }>;
}

interface ShopifyProductNode {
  title: string;
  featuredImage: { url: string } | null;
  variants: { nodes: Array<{ sku: string | null }> };
}

interface ShopifyProductsResponse {
  data?: {
    products?: {
      nodes: ShopifyProductNode[];
    };
  };
  errors?: Array<{ message: string }>;
}

// ============================================================================
// Metrics & Dimensions
// ============================================================================

export const SHOPIFY_METRICS: MetricDefinition[] = [
  // ── Existing metrics (Story 12.4) ──
  { key: "total_sales", label: "Total Revenue (Gross)", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"] },
  { key: "net_sales", label: "Net Sales", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "orders", label: "Orders", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"] },
  { key: "average_order_value", label: "Average Order Value", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "total_sales", denominator: "orders" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  { key: "units_sold", label: "Units Sold", type: "number", category: "Commerce", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart", "data-table"] },
  { key: "customers", label: "Customers", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "sessions", label: "Sessions", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "conversion_rate", label: "Store Conversion Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "orders", denominator: "sessions" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  { key: "product_revenue", label: "Product Revenue", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["bar-chart", "data-table"] },
  { key: "product_units_sold", label: "Product Units Sold", type: "number", category: "Commerce", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart", "data-table"] },
  { key: "percent_of_total", label: "% of Total Revenue", type: "percentage", category: "Revenue", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "product_revenue", denominator: "total_sales" }, compatibleWidgets: ["data-table"] },
  // product_sku is a string value (e.g. "SKU-001") exposed as a metric for data-table column selection.
  // MetricDefinition constrains type to numeric kinds; "number" is used as the closest fit. Rendered as text in DataTableWidget.
  { key: "product_sku", label: "SKU", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "average_selling_price", label: "Avg Selling Price", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "product_revenue", denominator: "units_sold" }, compatibleWidgets: ["data-table"] },
  { key: "refund_return_rate", label: "Refund/Return Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "orders" }, compatibleWidgets: ["data-table"] },

  // ── IMC-16: Sales metrics (Task 1) ──
  { key: "gross_sales", label: "Gross Sales", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"] },
  { key: "discounts", label: "Discounts", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"] },
  { key: "taxes", label: "Taxes", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "shipping", label: "Shipping Revenue", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "duties", label: "Duties", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "tips", label: "Tips", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "gift_card_gross_sales", label: "Gift Card Gross Sales", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "total_sales_first_time", label: "Total Sales (First-time)", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "total_sales_returning", label: "Total Sales (Returning)", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ── IMC-16: Profit metrics (Task 2) ──
  { key: "gross_profit", label: "Gross Profit", type: "currency", category: "Profit", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "gross_margin", label: "Gross Margin", type: "percentage", category: "Profit", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "gross_profit", denominator: "net_sales" }, compatibleWidgets: ["kpi", "line-chart"] },
  { key: "cost_of_goods_sold", label: "Cost of Goods Sold", type: "currency", category: "Profit", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "data-table"] },

  // ── IMC-16: Order detail metrics (Task 3) ──
  { key: "orders_first_time", label: "Orders (First-time)", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "orders_returning", label: "Orders (Returning)", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "orders_delivered", label: "Orders Delivered", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "orders_shipped", label: "Orders Shipped", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "ordered_quantity", label: "Ordered Quantity", type: "number", category: "Commerce", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "returned_quantity", label: "Returned Quantity", type: "number", category: "Commerce", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "net_quantity", label: "Net Quantity", type: "number", category: "Commerce", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "sales_reversals", label: "Sales Reversals", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "data-table"] },

  // ── IMC-16: Session metrics (Task 4) ──
  { key: "cart_abandonment_rate", label: "Cart Abandonment Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["kpi"] },
  { key: "growth_rate", label: "Growth Rate", type: "percentage", category: "Growth", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["kpi"] },

  // ── IMC-16: Customer metrics (Task 5) ──
  { key: "new_customers", label: "New Customers", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "returning_customers", label: "Returning Customers", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "customer_ltv", label: "Top Customer LTV (Avg)", type: "currency", category: "Audience", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "total_sales", denominator: "customers" }, compatibleWidgets: ["kpi", "data-table", "bar-chart"] },
  { key: "customer_order_count", label: "Customer Order Count", type: "number", category: "Audience", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "orders", denominator: "customers" }, compatibleWidgets: ["kpi", "data-table"] },
  { key: "customer_avg_order_amount", label: "Customer Avg Order Amount", type: "currency", category: "Audience", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { denominator: "orders" }, compatibleWidgets: ["kpi", "data-table"] },

  // ── IMC-16: Abandoned checkouts (Task 6) ──
  { key: "abandoned_checkout_count", label: "Abandoned Checkouts", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "abandoned_checkout_value", label: "Abandoned Checkout Value", type: "currency", category: "Conversion", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ── IMC-16: Inventory metrics (Task 7) ──
  { key: "inventory_quantity", label: "Inventory Quantity", type: "number", category: "Inventory", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "inventory_value", label: "Inventory Value", type: "currency", category: "Inventory", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
];

export const SHOPIFY_DIMENSIONS: DimensionDefinition[] = [
  // ── Time ──
  { key: "day", label: "Date", type: "Time" },
  { key: "day_of_week", label: "Day of Week", type: "Time" },
  { key: "month", label: "Month", type: "Time" },
  { key: "week", label: "Week", type: "Time" },
  { key: "hour", label: "Hour", type: "Time" },

  // ── Product ──
  { key: "product_title", label: "Product", type: "Content" },
  { key: "product_variant_sku", label: "SKU", type: "Content" },
  { key: "product_type", label: "Product Type", type: "Content" },
  { key: "product_vendor", label: "Product Vendor", type: "Content" },
  { key: "product_variant_title", label: "Variant", type: "Content" },
  { key: "product_collection", label: "Collection", type: "Content" },
  { key: "product_tag", label: "Product Tag", type: "Content" },
  { key: "product_category", label: "Product Category", type: "Content" },
  { key: "product_variant_abc_grade", label: "ABC Grade", type: "Content" },
  { key: "bundle_title", label: "Bundle", type: "Content" },

  // ── Geography (billing) ──
  { key: "billing_country", label: "Billing Country", type: "Geo" },
  { key: "billing_region", label: "Billing Region", type: "Geo" },
  { key: "billing_city", label: "Billing City", type: "Geo" },
  { key: "billing_postal_code", label: "Billing Postal Code", type: "Geo" },

  // ── Geography (shipping) ──
  { key: "shipping_country", label: "Shipping Country", type: "Geo" },
  { key: "shipping_region", label: "Shipping Region", type: "Geo" },
  { key: "shipping_city", label: "Shipping City", type: "Geo" },
  { key: "shipping_carrier", label: "Shipping Carrier", type: "Content" },

  // ── Channel ──
  { key: "sales_channel", label: "Sales Channel", type: "Channel" },
  { key: "sub_channel", label: "Sub-Channel", type: "Channel" },
  { key: "channel_handle", label: "Channel Handle", type: "Channel" },
  { key: "market", label: "Market", type: "Channel" },
  { key: "subscription_or_one_time", label: "Subscription / One-Time", type: "Channel" },

  // ── Discount ──
  { key: "discount_code", label: "Discount Code", type: "Content" },
  { key: "discount_name", label: "Discount Name", type: "Content" },
  { key: "discount_type", label: "Discount Type", type: "Content" },
  { key: "discount_class", label: "Discount Class", type: "Content" },

  // ── Traffic (sessions table) ──
  { key: "referrer_source", label: "Referrer Source", type: "Traffic" },
  { key: "referrer_name", label: "Referrer Name", type: "Traffic" },
  { key: "referrer_domain", label: "Referrer Domain", type: "Traffic" },
  { key: "landing_page_path", label: "Landing Page", type: "Traffic" },
  { key: "landing_page_type", label: "Landing Page Type", type: "Traffic" },

  // ── UTM (GraphQL customerJourney) ──
  { key: "first_visit_utm_source", label: "First Visit UTM Source", type: "Traffic" },
  { key: "first_visit_utm_medium", label: "First Visit UTM Medium", type: "Traffic" },
  { key: "first_visit_utm_campaign", label: "First Visit UTM Campaign", type: "Traffic" },
  { key: "last_visit_utm_source", label: "Last Visit UTM Source", type: "Traffic" },
  { key: "last_visit_utm_medium", label: "Last Visit UTM Medium", type: "Traffic" },
  { key: "last_visit_utm_campaign", label: "Last Visit UTM Campaign", type: "Traffic" },

  // ── Order Status ──
  { key: "fulfillment_status", label: "Fulfillment Status", type: "Content" },
  { key: "payment_status", label: "Payment Status", type: "Content" },
  { key: "is_canceled_order", label: "Canceled Order", type: "Content" },
  { key: "order_risk_level", label: "Order Risk Level", type: "Content" },
  { key: "return_reason", label: "Return Reason", type: "Content" },
  { key: "order_or_return", label: "Order or Return", type: "Content" },

  // ── Payment ──
  { key: "payment_method", label: "Payment Method", type: "Content" },
  { key: "digital_wallet", label: "Digital Wallet", type: "Content" },
  { key: "credit_card_type", label: "Credit Card Type", type: "Content" },

  // ── Customer ──
  { key: "customer_type", label: "Customer Type", type: "Audience" },
  { key: "customer_cohort_month", label: "Customer Cohort Month", type: "Audience" },
  { key: "customer_cohort_week", label: "Customer Cohort Week", type: "Audience" },
  { key: "rfm_group", label: "RFM Group", type: "Audience" },
  { key: "predicted_spend_tier", label: "Predicted Spend Tier", type: "Audience" },

  // ── Session ──
  { key: "session_country", label: "Session Country", type: "Geo" },
  { key: "session_region", label: "Session Region", type: "Geo" },
  { key: "session_city", label: "Session City", type: "Geo" },
  { key: "session_bounced", label: "Session Bounced", type: "Content" },

  // ── POS ──
  { key: "pos_location_name", label: "POS Location", type: "Content" },
];

// ============================================================================
// ShopifyQL Helpers
// ============================================================================

/**
 * Parse a ShopifyQL cell value based on column data type.
 * CRITICAL: All ShopifyQL row cells are strings regardless of data type.
 */
function parseCell(value: string | null | undefined, dataType: string): string | number {
  if (value === null || value === undefined || value === "") {
    return dataType === "date" || dataType === "string" ? "" : 0;
  }

  switch (dataType) {
    case "number":
    case "currency":
    case "percentage": {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    case "date": {
      // Normalize to YYYY-MM-DD (ShopifyQL date format varies by store locale)
      try {
        return new Date(value).toISOString().slice(0, 10);
      } catch {
        return value;
      }
    }
    default:
      return value;
  }
}

/**
 * Parse a ShopifyQL table response into an array of objects.
 */
function parseTableData(tableData: ShopifyQLTableData): Record<string, string | number>[] {
  const { columns, rows } = tableData;
  return rows.map((row) => {
    const record: Record<string, string | number> = {};
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i]!;
      record[col.name] = parseCell(row[i], col.dataType);
    }
    return record;
  });
}

/**
 * Parse GraphQL response for errors and rate limit info.
 */
function parseShopifyGraphQLResponse(body: ShopifyQLResponse): {
  error: AdapterResult<never> | null;
  delayMs: number;
} {
  // Check for GraphQL errors
  if (body.errors && body.errors.length > 0) {
    const firstError = body.errors[0]!;
    const code = firstError.extensions?.code;

    if (code === "THROTTLED") {
      return {
        error: adapterErr({
          code: "rate_limited",
          message: sanitizeErrorMessage(firstError.message),
          retryable: true,
        }),
        delayMs: 0,
      };
    }

    if (code === "ACCESS_DENIED") {
      return {
        error: adapterErr({
          code: "permission_denied",
          message: "ShopifyQL requires Shopify plan or higher. Basic plan stores have limited reporting.",
          retryable: false,
        }),
        delayMs: 0,
      };
    }

    if (code === "INTERNAL_SERVER_ERROR") {
      return {
        error: adapterErr({
          code: "provider_unavailable",
          message: sanitizeErrorMessage(firstError.message),
          retryable: true,
        }),
        delayMs: 0,
      };
    }

    return {
      error: adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(firstError.message),
        retryable: false,
      }),
      delayMs: 0,
    };
  }

  // Check cost-based rate limit (soft throttle)
  let delayMs = 0;
  const throttleStatus = body.extensions?.cost?.throttleStatus;
  if (throttleStatus) {
    const requestedCost = body.extensions?.cost?.requestedQueryCost ?? 0;
    if (throttleStatus.currentlyAvailable < requestedCost) {
      delayMs = Math.ceil(
        ((requestedCost - throttleStatus.currentlyAvailable) /
          throttleStatus.restoreRate) *
          1000
      );
    }
  }

  return { error: null, delayMs };
}

/**
 * Execute a ShopifyQL query via GraphQL.
 */
async function executeShopifyQL(
  shop: string,
  accessToken: string,
  query: string
): Promise<AdapterResult<Record<string, string | number>[]>> {
  // SSRF defense: validate shop hostname before constructing URL
  if (!SHOP_DOMAIN_REGEX.test(shop)) {
    return adapterErr({
      code: "invalid_response",
      message: "Invalid shop domain",
      retryable: false,
    });
  }

  const graphqlUrl = `https://${shop}/admin/api/${SHOPIFY_API_VERSION}/graphql.json`;

  const graphqlQuery = `{
    shopifyqlQuery(query: ${JSON.stringify(query)}) {
      tableData {
        columns { name dataType displayName }
        rows
      }
      parseErrors
    }
  }`;

  const result = await safeFetch(
    graphqlUrl,
    {
      method: "POST",
      headers: {
        "X-Shopify-Access-Token": accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query: graphqlQuery }),
    },
    API_TIMEOUT_MS
  );

  if (!result.success) {
    return adapterErr({
      code: "invalid_response",
      message: sanitizeErrorMessage(result.error.message),
      retryable: true,
    });
  }

  const response = result.data;
  if (!response.ok) {
    if (response.status === 429) {
      return adapterErr({
        code: "rate_limited",
        message: "Shopify rate limit exceeded",
        retryable: true,
      });
    }
    return adapterErr({
      code: "provider_unavailable",
      message: `Shopify API error: HTTP ${response.status}`,
      retryable: response.status >= 500,
    });
  }

  const body = (await response.json()) as ShopifyQLResponse;

  // Check GraphQL-level errors
  const { error, delayMs } = parseShopifyGraphQLResponse(body);
  if (error) return error;

  // Wait if soft throttled
  if (delayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  // Check parse errors
  const queryData = body.data?.shopifyqlQuery;
  if (queryData?.parseErrors && queryData.parseErrors.length > 0) {
    return adapterErr({
      code: "invalid_response",
      message: `ShopifyQL parse error: ${queryData.parseErrors[0]}`,
      retryable: false,
    });
  }

  // Handle empty/null tableData
  if (!queryData?.tableData) {
    return adapterOk([]);
  }

  const records = parseTableData(queryData.tableData);
  return adapterOk(records);
}

// ============================================================================
// Product Catalog Helper (Story 12.6)
// ============================================================================

/**
 * Fetch product images and SKUs from Shopify GraphQL Admin API.
 * Non-blocking: returns empty map on any failure (scope missing, rate limit, etc.).
 */
async function fetchProductCatalogData(
  shop: string,
  accessToken: string,
  limit: number
): Promise<Map<string, { sku: string; imageUrl: string }>> {
  try {
    if (!SHOP_DOMAIN_REGEX.test(shop)) return new Map();

    const graphqlUrl = `https://${shop}/admin/api/${SHOPIFY_API_VERSION}/graphql.json`;
    const query = `query GetProducts($first: Int!) {
      products(first: $first, sortKey: BEST_SELLING) {
        nodes {
          title
          featuredImage { url }
          variants(first: 1) { nodes { sku } }
        }
      }
    }`;

    const result = await safeFetch(
      graphqlUrl,
      {
        method: "POST",
        headers: {
          "X-Shopify-Access-Token": accessToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query, variables: { first: limit } }),
      },
      API_TIMEOUT_MS
    );

    if (!result.success || !result.data.ok) return new Map();

    const body = (await result.data.json()) as ShopifyProductsResponse;
    if (body.errors && body.errors.length > 0) return new Map();

    const nodes = body.data?.products?.nodes ?? [];
    const catalogMap = new Map<string, { sku: string; imageUrl: string }>();

    for (const node of nodes) {
      catalogMap.set(node.title, {
        sku: node.variants.nodes[0]?.sku ?? "",
        imageUrl: node.featuredImage?.url ?? "",
      });
    }

    return catalogMap;
  } catch {
    return new Map();
  }
}

// ============================================================================
// GraphQL Query Helpers (IMC-16: Tasks 5-7)
// ============================================================================

/**
 * Execute a raw GraphQL query against the Shopify Admin API.
 * Returns the parsed JSON body or null on failure (non-blocking).
 */
async function executeGraphQL<T>(
  shop: string,
  accessToken: string,
  query: string,
  variables?: Record<string, unknown>
): Promise<T | null> {
  try {
    if (!SHOP_DOMAIN_REGEX.test(shop)) return null;

    const graphqlUrl = `https://${shop}/admin/api/${SHOPIFY_API_VERSION}/graphql.json`;
    const result = await safeFetch(
      graphqlUrl,
      {
        method: "POST",
        headers: {
          "X-Shopify-Access-Token": accessToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(variables ? { query, variables } : { query }),
      },
      API_TIMEOUT_MS
    );

    if (!result.success || !result.data.ok) return null;
    return (await result.data.json()) as T;
  } catch {
    return null;
  }
}

/**
 * Fetch abandoned checkout count via GraphQL.
 * Non-blocking: returns 0 on failure.
 */
async function fetchAbandonedCheckoutCount(
  shop: string,
  accessToken: string
): Promise<number> {
  const query = `{ abandonedCheckoutsCount { count } }`;
  const body = await executeGraphQL<ShopifyAbandonedCheckoutsCountResponse>(shop, accessToken, query);
  return body?.data?.abandonedCheckoutsCount?.count ?? 0;
}

/**
 * Fetch abandoned checkout values via GraphQL.
 * Returns total value of abandoned checkouts. Non-blocking.
 * NOTE: Limited to first 100 abandoned checkouts. Stores with high volume
 * may have underreported totals. Pagination can be added if needed.
 */
async function fetchAbandonedCheckoutValue(
  shop: string,
  accessToken: string
): Promise<number> {
  const query = `{
    abandonedCheckouts(first: 100, sortKey: CREATED_AT, reverse: true) {
      nodes {
        subtotalPriceSet { shopMoney { amount } }
      }
    }
  }`;
  const body = await executeGraphQL<ShopifyAbandonedCheckoutsResponse>(shop, accessToken, query);
  const nodes = body?.data?.abandonedCheckouts?.nodes ?? [];
  let total = 0;
  for (const node of nodes) {
    total += parseFloat(node.subtotalPriceSet?.shopMoney?.amount ?? "0") || 0;
  }
  return total;
}

/**
 * Fetch top customers by total spent for CLV analysis.
 * Returns aggregated CLV data. Non-blocking.
 * NOTE: Samples top N customers by spend — reflects high-value customer segment,
 * not store-wide average. Useful for benchmarking best customers.
 */
async function fetchCustomerLTVData(
  shop: string,
  accessToken: string,
  limit: number
): Promise<{ avgLTV: number; avgOrderCount: number; avgOrderAmount: number }> {
  const query = `query GetTopCustomers($first: Int!) {
    customers(first: $first, sortKey: TOTAL_SPENT, reverse: true) {
      nodes {
        amountSpent { amount currencyCode }
        numberOfOrders
      }
    }
  }`;
  const body = await executeGraphQL<ShopifyCustomersResponse>(shop, accessToken, query, { first: limit });
  const nodes = body?.data?.customers?.nodes ?? [];

  if (nodes.length === 0) return { avgLTV: 0, avgOrderCount: 0, avgOrderAmount: 0 };

  let totalSpent = 0;
  let totalOrders = 0;
  for (const node of nodes) {
    const spent = parseFloat(node.amountSpent?.amount ?? "0") || 0;
    const orders = parseInt(node.numberOfOrders ?? "0", 10) || 0;
    totalSpent += spent;
    totalOrders += orders;
  }

  const avgLTV = totalSpent / nodes.length;
  const avgOrderCount = totalOrders / nodes.length;
  const avgOrderAmount = totalOrders > 0 ? totalSpent / totalOrders : 0;

  return { avgLTV, avgOrderCount, avgOrderAmount };
}

/**
 * Fetch inventory levels via GraphQL.
 * Returns total available quantity and estimated value. Non-blocking.
 */
async function fetchInventoryData(
  shop: string,
  accessToken: string
): Promise<{ totalQuantity: number; totalValue: number }> {
  const query = `{
    inventoryItems(first: 100) {
      nodes {
        unitCost { amount }
        inventoryLevels(first: 10) {
          nodes {
            quantities(names: ["available"]) { name quantity }
          }
        }
      }
    }
  }`;
  const body = await executeGraphQL<ShopifyInventoryResponse>(shop, accessToken, query);
  const nodes = body?.data?.inventoryItems?.nodes ?? [];

  let totalQuantity = 0;
  let totalValue = 0;
  for (const item of nodes) {
    const unitCost = parseFloat(item.unitCost?.amount ?? "0") || 0;
    for (const level of item.inventoryLevels.nodes) {
      for (const qty of level.quantities) {
        if (qty.name === "available") {
          totalQuantity += qty.quantity;
          totalValue += qty.quantity * unitCost;
        }
      }
    }
  }

  return { totalQuantity, totalValue };
}

// ============================================================================
// Adapter Implementation
// ============================================================================

/**
 * Extract and validate providerAccountId from adapter context.
 */
function getShopDomain(ctx: AdapterContext): string | undefined {
  const shop = (ctx.env as unknown as Record<string, string>)
    ?.providerAccountId;
  if (typeof shop !== "string" || !SHOP_DOMAIN_REGEX.test(shop)) {
    return undefined;
  }
  return shop;
}

export const shopifyAdapter: IntegrationAdapter = {
  platform: "shopify",

  rateLimits: {
    requestsPerMinute: 120,
    burstLimit: 1000,
    retryAfterMs: 1000,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const accessToken = credentials.accessToken;
    const shop = getShopDomain(ctx);

    if (!shop) {
      return adapterErr({
        code: "invalid_response",
        message: "Missing shop domain (providerAccountId)",
        retryable: false,
      });
    }

    const startStr = dateRange.start.toISOString().slice(0, 10);
    const endStr = dateRange.end.toISOString().slice(0, 10);

    // ── ShopifyQL Query 1: Expanded daily overview (Tasks 1-3) ──
    // Added: gross_sales, discounts, taxes, shipping, duties, tips, gift_card_gross_sales,
    //        gross_profit, gross_margin, cost_of_goods_sold, ordered_quantity, returned_quantity,
    //        net_quantity, orders_delivered, orders_shipped, sales_reversals
    const dailyQuery = `FROM sales SHOW total_sales, net_sales, gross_sales, orders, average_order_value, units_sold, customers, sessions, conversion_rate, discounts, taxes, shipping, duties, tips, gift_card_gross_sales, gross_profit, gross_margin, cost_of_goods_sold, ordered_quantity, returned_quantity, net_quantity, orders_delivered, orders_shipped, sales_reversals SINCE ${startStr} UNTIL ${endStr} GROUP BY day ORDER BY day`;
    const dailyResult = await executeShopifyQL(shop, accessToken, dailyQuery);

    if (!dailyResult.success) return dailyResult;

    // ── ShopifyQL Query 2: Top products (existing, expanded with sales_reversals) ──
    const productLimit = 50;
    const productsQuery = `FROM sales SHOW total_sales, units_sold, sales_reversals GROUP BY product_title ORDER BY total_sales DESC LIMIT ${productLimit}`;
    const productsResult = await executeShopifyQL(shop, accessToken, productsQuery);

    if (!productsResult.success) return productsResult;

    // ── ShopifyQL Query 3: Customer type split (Task 1 + 3 + 5) ──
    const firstTimeQuery = `FROM sales SHOW total_sales, orders, customers WHERE customer_type = 'First-time' SINCE ${startStr} UNTIL ${endStr}`;
    const returningQuery = `FROM sales SHOW total_sales, orders, customers WHERE customer_type = 'Returning' SINCE ${startStr} UNTIL ${endStr}`;

    // ── ShopifyQL Query 4: Session computed metrics (Task 4) ──
    // cart_abandonment_rate and growth_rate are computed — no ORDER BY day
    const sessionComputedQuery = `FROM sales SHOW cart_abandonment_rate, growth_rate SINCE ${startStr} UNTIL ${endStr}`;

    // Execute queries 3-4 in parallel with product catalog and GraphQL queries
    const [
      firstTimeResult,
      returningResult,
      sessionComputedResult,
      catalogMap,
      abandonedCount,
      abandonedValue,
      customerLTVData,
      inventoryData,
    ] = await Promise.all([
      executeShopifyQL(shop, accessToken, firstTimeQuery),
      executeShopifyQL(shop, accessToken, returningQuery),
      executeShopifyQL(shop, accessToken, sessionComputedQuery),
      fetchProductCatalogData(shop, accessToken, productLimit),
      fetchAbandonedCheckoutCount(shop, accessToken),
      fetchAbandonedCheckoutValue(shop, accessToken),
      fetchCustomerLTVData(shop, accessToken, 100),
      fetchInventoryData(shop, accessToken),
    ]);

    // Extract customer type splits (non-fatal on failure)
    const firstTimeRow = firstTimeResult.success ? firstTimeResult.data[0] : undefined;
    const returningRow = returningResult.success ? returningResult.data[0] : undefined;
    const totalSalesFirstTime = Number(firstTimeRow?.total_sales ?? 0);
    const totalSalesReturning = Number(returningRow?.total_sales ?? 0);
    const ordersFirstTime = Number(firstTimeRow?.orders ?? 0);
    const ordersReturning = Number(returningRow?.orders ?? 0);
    const newCustomers = Number(firstTimeRow?.customers ?? 0);
    const returningCustomers = Number(returningRow?.customers ?? 0);

    // Extract session computed metrics (non-fatal on failure)
    const sessionRow = sessionComputedResult.success ? sessionComputedResult.data[0] : undefined;
    const cartAbandonmentRate = Number(sessionRow?.cart_abandonment_rate ?? 0);
    const growthRate = Number(sessionRow?.growth_rate ?? 0);

    // Fetch currency from shop info (via env or default)
    const currencyCode = (ctx.env as unknown as Record<string, string>).currencyCode || "USD";

    const records: Record<string, unknown>[] = [];

    // ── Build daily overview records (expanded with all new metrics) ──
    let totalRevenue = 0;
    let totalNetSales = 0;
    let totalGrossSales = 0;
    let totalOrders = 0;
    let totalUnitsSold = 0;
    let totalCustomers = 0;
    let totalSessions = 0;
    let totalDiscounts = 0;
    let totalTaxes = 0;
    let totalShipping = 0;
    let totalDuties = 0;
    let totalTips = 0;
    let totalGiftCardGrossSales = 0;
    let totalGrossProfit = 0;
    let totalCogs = 0;
    let totalOrderedQty = 0;
    let totalReturnedQty = 0;
    let totalNetQty = 0;
    let totalOrdersDelivered = 0;
    let totalOrdersShipped = 0;
    let totalSalesReversals = 0;

    for (const row of dailyResult.data) {
      const revenue = Number(row.total_sales) || 0;
      const netSales = Number(row.net_sales) || 0;
      const grossSales = Number(row.gross_sales) || 0;
      const orders = Number(row.orders) || 0;
      const aov = Number(row.average_order_value) || 0; // Use ShopifyQL native AOV (Task 3.4)
      const unitsSold = Number(row.units_sold) || 0;
      const customers = Number(row.customers) || 0;
      const sessions = Number(row.sessions) || 0;
      const conversionRate = Number(row.conversion_rate) || 0;
      const discounts = Number(row.discounts) || 0;
      const taxes = Number(row.taxes) || 0;
      const shipping = Number(row.shipping) || 0;
      const duties = Number(row.duties) || 0;
      const tips = Number(row.tips) || 0;
      const giftCardGrossSales = Number(row.gift_card_gross_sales) || 0;
      const grossProfit = Number(row.gross_profit) || 0;
      const grossMargin = Number(row.gross_margin) || 0;
      const cogs = Number(row.cost_of_goods_sold) || 0;
      const orderedQty = Number(row.ordered_quantity) || 0;
      const returnedQty = Number(row.returned_quantity) || 0;
      const netQty = Number(row.net_quantity) || 0;
      const ordersDelivered = Number(row.orders_delivered) || 0;
      const ordersShipped = Number(row.orders_shipped) || 0;
      const salesReversals = Number(row.sales_reversals) || 0;

      totalRevenue += revenue;
      totalNetSales += netSales;
      totalGrossSales += grossSales;
      totalOrders += orders;
      totalUnitsSold += unitsSold;
      totalCustomers += customers;
      totalSessions += sessions;
      totalDiscounts += discounts;
      totalTaxes += taxes;
      totalShipping += shipping;
      totalDuties += duties;
      totalTips += tips;
      totalGiftCardGrossSales += giftCardGrossSales;
      totalGrossProfit += grossProfit;
      totalCogs += cogs;
      totalOrderedQty += orderedQty;
      totalReturnedQty += returnedQty;
      totalNetQty += netQty;
      totalOrdersDelivered += ordersDelivered;
      totalOrdersShipped += ordersShipped;
      totalSalesReversals += salesReversals;

      records.push({
        _reportType: "dailyOverview",
        date: String(row.day || ""),
        revenue,
        netSales,
        grossSales,
        orders,
        aov,
        unitsSold,
        customers,
        sessions,
        conversionRate,
        discounts,
        taxes,
        shipping,
        duties,
        tips,
        giftCardGrossSales,
        grossProfit,
        grossMargin,
        costOfGoodsSold: cogs,
        orderedQuantity: orderedQty,
        returnedQuantity: returnedQty,
        netQuantity: netQty,
        ordersDelivered,
        ordersShipped,
        salesReversals,
        currencyCode,
      });
    }

    // ── Build summary record ──
    const summaryGrossMargin = totalGrossSales > 0 ? totalGrossProfit / totalGrossSales : 0;
    const summaryConversionRate =
      totalSessions > 0 ? totalOrders / totalSessions : 0;

    records.push({
      _reportType: "summary",
      totalRevenue,
      netSales: totalNetSales,
      grossSales: totalGrossSales,
      orderCount: totalOrders,
      aov: totalOrders > 0 ? totalRevenue / totalOrders : 0,
      unitsSold: totalUnitsSold,
      customerCount: totalCustomers,
      sessions: totalSessions,
      conversionRate: summaryConversionRate,
      discounts: totalDiscounts,
      taxes: totalTaxes,
      shipping: totalShipping,
      duties: totalDuties,
      tips: totalTips,
      giftCardGrossSales: totalGiftCardGrossSales,
      grossProfit: totalGrossProfit,
      grossMargin: summaryGrossMargin,
      costOfGoodsSold: totalCogs,
      orderedQuantity: totalOrderedQty,
      returnedQuantity: totalReturnedQty,
      netQuantity: totalNetQty,
      ordersDelivered: totalOrdersDelivered,
      ordersShipped: totalOrdersShipped,
      salesReversals: totalSalesReversals,
      // Customer type splits
      totalSalesFirstTime,
      totalSalesReturning,
      ordersFirstTime,
      ordersReturning,
      newCustomers,
      returningCustomers,
      // Session computed metrics
      cartAbandonmentRate,
      growthRate,
      // GraphQL data
      customerLtv: customerLTVData.avgLTV,
      customerOrderCount: customerLTVData.avgOrderCount,
      customerAvgOrderAmount: customerLTVData.avgOrderAmount,
      abandonedCheckoutCount: abandonedCount,
      abandonedCheckoutValue: abandonedValue,
      inventoryQuantity: inventoryData.totalQuantity,
      inventoryValue: inventoryData.totalValue,
      currencyCode,
    });

    // ── Build top product records (enhanced with catalog data — Story 12.6) ──
    for (const row of productsResult.data) {
      const productTitle = String(row.product_title || "");
      const revenue = Number(row.total_sales) || 0;
      const unitsSold = Number(row.units_sold) || 0;
      const percentOfTotal = totalRevenue > 0 ? revenue / totalRevenue : 0;
      const salesReversals = Number(row.sales_reversals) || 0;
      const catalog = catalogMap.get(productTitle);

      records.push({
        _reportType: "topProduct",
        productTitle,
        productSku: catalog?.sku ?? "",
        revenue,
        unitsSold,
        averageSellingPrice: unitsSold > 0 ? revenue / unitsSold : 0,
        refundReturnRate: revenue > 0 ? salesReversals / revenue : 0,
        percentOfTotal,
        currencyCode,
        _productImageUrl: catalog?.imageUrl ?? "",
      });
    }

    // ── Abandoned checkouts records (Task 6) ──
    records.push({
      _reportType: "abandonedCheckouts",
      abandonedCheckoutCount: abandonedCount,
      abandonedCheckoutValue: abandonedValue,
      currencyCode,
    });

    // ── Inventory records (Task 7) ──
    records.push({
      _reportType: "inventory",
      inventoryQuantity: inventoryData.totalQuantity,
      inventoryValue: inventoryData.totalValue,
      currencyCode,
    });

    return adapterOk({
      platform: "shopify",
      accountId: shop,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const shop = getShopDomain(ctx);

    if (!shop) {
      return adapterErr({
        code: "invalid_response",
        message: "Missing shop domain",
        retryable: false,
      });
    }

    const graphqlUrl = `https://${shop}/admin/api/${SHOPIFY_API_VERSION}/graphql.json`;
    const query = `{ shop { name } }`;

    const result = await safeFetch(
      graphqlUrl,
      {
        method: "POST",
        headers: {
          "X-Shopify-Access-Token": credentials.accessToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
      },
      10_000
    );

    if (!result.success) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(result.error.message),
        retryable: true,
      });
    }

    if (!result.data.ok) {
      if (result.data.status === 401) {
        return adapterErr({
          code: "token_expired",
          message: "Shopify access token is invalid or revoked",
          retryable: false,
        });
      }
      return adapterErr({
        code: "provider_unavailable",
        message: `Shopify API error: HTTP ${result.data.status}`,
        retryable: true,
      });
    }

    const body = (await result.data.json()) as ShopifyShopResponse;
    if (body.errors && body.errors.length > 0) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(body.errors[0]?.message ?? "Unknown error"),
        retryable: false,
      });
    }

    return adapterOk(!!body.data?.shop?.name);
  },

  // No refreshToken — Shopify offline access tokens are permanent

  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    const shop = getShopDomain(ctx);

    if (!shop) {
      return adapterErr({
        code: "invalid_response",
        message: "Missing shop domain",
        retryable: false,
      });
    }

    // Shopify has exactly 1 account per OAuth token (the store).
    // currentAppInstallation.accessScopes returns the GRANTED scopes for this
    // installation (Story 31-7) — same single request, no extra round trip.
    const graphqlUrl = `https://${shop}/admin/api/${SHOPIFY_API_VERSION}/graphql.json`;
    const query = `{ shop { name myshopifyDomain } currentAppInstallation { accessScopes { handle } } }`;

    const result = await safeFetch(
      graphqlUrl,
      {
        method: "POST",
        headers: {
          "X-Shopify-Access-Token": credentials.accessToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
      },
      10_000
    );

    if (!result.success) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(result.error.message),
        retryable: true,
      });
    }

    if (!result.data.ok) {
      // Token revoked / app uninstalled — align with validateConnection
      if (result.data.status === 401) {
        return adapterErr({
          code: "token_expired",
          message: "Shopify access token is invalid or revoked",
          retryable: false,
        });
      }
      return adapterErr({
        code: "provider_unavailable",
        message: `Shopify API error: HTTP ${result.data.status}`,
        retryable: true,
      });
    }

    const body = (await result.data.json()) as {
      data?: {
        shop?: { name: string; myshopifyDomain: string };
        currentAppInstallation?: { accessScopes?: Array<{ handle: string }> };
      };
      errors?: Array<{ message: string }>;
    };

    // GraphQL `errors` are fatal ONLY when shop identity is also missing.
    // Per the GraphQL spec, a field-level failure nulls that field and
    // appends to `errors` while the rest of `data` survives — so a
    // populated `shop` alongside an `errors` entry is exactly the
    // partial-response case of Story 31-7 AC#2: discovery MUST NOT fail
    // when shop identity succeeded but scope enrichment didn't (the
    // fallback chain below handles the missing scopes instead).
    const shopData = body.data?.shop;
    if (!shopData) {
      if (body.errors && body.errors.length > 0) {
        return adapterErr({
          code: "invalid_response",
          message: sanitizeErrorMessage(
            body.errors[0]?.message ?? "Unknown error"
          ),
          retryable: false,
        });
      }
      return adapterErr({
        code: "invalid_response",
        message: "Could not fetch shop info",
        retryable: false,
      });
    }

    // Granted scopes from the live API (never the SHOPIFY_SCOPES request
    // constant). Partial-response fallback chain (Story 31-7 AC#2):
    // accessScopes handles → credentials.scopes (API-sourced at token
    // exchange) → []. Scope enrichment failing MUST NOT fail discovery.
    const accessScopes = body.data?.currentAppInstallation?.accessScopes;
    const scopes =
      accessScopes && accessScopes.length > 0
        ? accessScopes.map((s) => s.handle)
        : (credentials.scopes ?? []);

    return adapterOk([
      {
        id: shopData.myshopifyDomain,
        name: shopData.name,
        platform: "shopify" as const,
        scopes,
      },
    ]);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return SHOPIFY_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return SHOPIFY_DIMENSIONS;
  },
};
